import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

interface StableSelectProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  style?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
}

export interface StableSelectRef {
  focus: () => void;
  blur: () => void;
}

const StableSelect = forwardRef<StableSelectRef, StableSelectProps>(({
  id,
  value,
  onChange,
  style,
  className,
  children
}, ref) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const isControlledUpdate = useRef(false);

  useImperativeHandle(ref, () => ({
    focus: () => selectRef.current?.focus(),
    blur: () => selectRef.current?.blur()
  }));

  // Update select value only when it differs from props
  useEffect(() => {
    if (selectRef.current && selectRef.current.value !== value) {
      isControlledUpdate.current = true;
      selectRef.current.value = value;
      isControlledUpdate.current = false;
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!isControlledUpdate.current) {
      onChange(e.target.value);
    }
  };

  return (
    <select
      ref={selectRef}
      id={id}
      defaultValue={value}
      onChange={handleChange}
      style={style}
      className={className}
      data-lpignore="true"
      data-1p-ignore="true"
      data-bwignore="true"
    >
      {children}
    </select>
  );
});

StableSelect.displayName = 'StableSelect';

export default StableSelect;